import React, { FC, useCallback, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ViewStyle, TouchableOpacity } from "react-native"
import { Button, Screen, Text, AutoFeederSetupNormalForm, AutoFeederSetupContinueForm } from "../../../../components"
import { color } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View, ScrollView } from "native-base"
import { SafeAreaView } from "react-native-safe-area-context"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import { goBack, navigate } from "../../../../navigators"
import { FormikProps, FormikValues } from "formik"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1
}

const DOUBLE_BUTTON: ViewStyle = {
  width: "48%"
}

const MODE_CONTAINER = (isActive): ViewStyle => ({
  borderWidth: 1,
  borderColor: isActive ? color.primary : color.palette.gray500,
  backgroundColor: isActive ? '#ECF5FF' : color.palette.white,
  alignItems: 'flex-start',
  padding: 12,
  width: '48%',
  borderRadius: 10
})

export const WizardModeSetupScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function WizardModeSetupScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const [ isNormal, setIsNormal ] = useState<boolean>(false)
  const [ isError, setIsError ] = useState<boolean>(true)
  const { autoFeederPondStore } = useStores()

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
    if (modelForm.current) setIsError(!(modelForm.current.isValid))
  }, [])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  const handleBack = useCallback(() => {
    goBack('Home.Stack')
  }, [])

  const onSubmit = useCallback(async (data) => {
    autoFeederPondStore.set('state', {...data, mode: isNormal ? 'normal' : 'continuous'})
    await delay(1)
    JalaAnalytic.log('Mobile App : Asto - Change Mode', { mode: isNormal ? 'normal' : 'continuous' });
    navigate('Home.Stack', 'Home.AutoFeederPond.Wizard.Test.Run')
  }, [autoFeederPondStore, isNormal])

  const CardMode = () => (
    <View flexDirection="row" justifyContent="space-between">
      <TouchableOpacity style={MODE_CONTAINER(!isNormal)} onPress={() => setIsNormal(false)}>
        <MaterialIcons name="data-usage" size={22} color={color.palette.gray800} />
        <Text tx="autoFeederPondCreateWizardScreen.continue" preset="title4" mb="4px" mt="9px" color={color.palette.gray800} />
        <Text tx="autoFeederPondCreateWizardScreen.continueDescription" preset="body3" color={color.palette.gray800} />
      </TouchableOpacity>
      <TouchableOpacity style={MODE_CONTAINER(isNormal)} onPress={() => setIsNormal(true)}>
        <MaterialIcons name="developer-mode" size={22} color={color.palette.gray800} />
        <Text tx="autoFeederPondCreateWizardScreen.normal" preset="title4" mb="4px" mt="9px" color={color.palette.gray800} />
        <Text tx="autoFeederPondCreateWizardScreen.normalDescription" preset="body3" color={color.palette.gray800} />
      </TouchableOpacity>
    </View>
  )

  const ModelForm = useCallback(() => {
    return (isNormal
      ? <AutoFeederSetupNormalForm
          colorScheme={colorScheme}
          initialValues={{ timesPerDay: 4, debit: 120, intervalPeriod: 60, onTime: 1, startTime: "00:00:00", endTime: "23:59:00" }}
          innerRef={modelFormRef}
          onSubmit={onSubmit}
        />
      : <AutoFeederSetupContinueForm
          colorScheme={colorScheme}
          initialValues={{ timesPerDay: 4, debit: 120, intervalPeriod: 60, onTime: 1, startTime: "00:00:00", endTime: "23:59:00" }}
          innerRef={modelFormRef}
          onSubmit={onSubmit}
        />
    )
  }, [colorScheme, isNormal, modelFormRef, onSubmit])

  return (
    <Screen style={ROOT} preset="fixed" colorScheme={colorScheme}>
      <SafeAreaView />
      <Text preset="body2" text={`3/4: ${translate("autoFeederPondCreateWizardScreen.modeSetup")}`} color={color.palette.gray800} mb="8px" ml="16px" />
      <View height="3px" backgroundColor={color.palette.gray300} borderRadius="5px" mx="16px">
        <View height="3px" backgroundColor={color.primary} width="75%" borderRadius="5px" />
      </View>
      <ScrollView flex={1} px="16px">
        <Text preset="title3" tx="autoFeederPondCreateWizardScreen.modeSetup" color={color.palette.gray900} mt="30px" />
        <Text preset="body2" tx="autoFeederPondCreateWizardScreen.modeSetupSubtitle" color={color.palette.gray700} mt="4px" mb="32px" />
        <CardMode />
        <ModelForm />
      </ScrollView>
      <View flexDirection="row" alignItems="center" padding="16px" justifyContent="space-between" borderTopWidth={1} borderColor={color.palette.gray400}>
        <Button
          testID="jala:id/left-button"
          tx="common.back"
          colorScheme={colorScheme}
          // loading={loading}
          onPress={handleBack}
          style={DOUBLE_BUTTON}
          preset="outline"
        />
        <Button
          testID="jala:id/right-button"
          tx="common.next"
          colorScheme={colorScheme}
          disabled={isError}
          // loading={loading}
          onPress={handleSubmit}
          style={DOUBLE_BUTTON}
        />
      </View>
    </Screen>
  )
})
